import { useState, useEffect, useCallback } from 'react';

export type ScreenType = 'mobile' | 'tablet' | 'desktop';

interface ScreenTypeResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenType: ScreenType;
}

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const;

function getScreenType(width: number): ScreenType {
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
}

/**
 * Centralized screen type detection hook.
 * Uses matchMedia for performance (no resize polling).
 * 
 * Breakpoints:
 *   mobile  : < 768px
 *   tablet  : 768px – 1023px
 *   desktop : >= 1024px
 */
export function useScreenType(): ScreenTypeResult {
  const [screenType, setScreenType] = useState<ScreenType>(() =>
    getScreenType(window.innerWidth)
  );

  const handleChange = useCallback(() => {
    setScreenType(getScreenType(window.innerWidth));
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobile - 1}px)`);
    const tabletQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.mobile}px) and (max-width: ${BREAKPOINTS.tablet - 1}px)`);

    // Modern browsers
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleChange);
      tabletQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mobileQuery.addListener(handleChange);
      tabletQuery.addListener(handleChange);
    }

    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', handleChange);
        tabletQuery.removeEventListener('change', handleChange);
      } else {
        mobileQuery.removeListener(handleChange);
        tabletQuery.removeListener(handleChange);
      }
    };
  }, [handleChange]);

  return {
    isMobile: screenType === 'mobile',
    isTablet: screenType === 'tablet',
    isDesktop: screenType === 'desktop',
    screenType,
  };
}

export default useScreenType;
